import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin, Clock } from "lucide-react";
import { useT } from "../../i18n";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t("contact.form.required");
    if (!form.email.trim()) errs.email = t("contact.form.required");
    else if (!emailRe.test(form.email)) errs.email = t("contact.form.invalidEmail");
    if (!form.message.trim()) errs.message = t("contact.form.required");
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSending(true);
    // Frontend-only: brief simulated pause then redirect
    setTimeout(() => {
      navigate("/thank_you");
    }, 400);
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-[#f6f4ee]" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="eyebrow mb-6">{t("contact.sectionLabel")}</div>
            <h2 className="font-serif text-[#1a211c] tracking-tight leading-[1.05] text-[clamp(2rem,5vw,4rem)]">
              {t("contact.title")}
            </h2>
            <p className="mt-6 text-[1.02rem] leading-relaxed text-[#1a211c]/75 max-w-md">
              {t("contact.subtitle")}
            </p>

            <div className="mt-12 space-y-6">
              <div className="text-[0.72rem] font-mono tracking-[0.24em] text-[#3d4f42] uppercase">
                {t("contact.sideTitle")}
              </div>
              <ul className="space-y-5 text-[0.98rem] text-[#1a211c]/85">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-1 text-[#3d4f42]" />
                  <span>{t("contact.address")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={16} className="mt-1 text-[#3d4f42]" />
                  <a
                    href={`mailto:${t("contact.email")}`}
                    className="link-underline"
                    data-testid="contact-email-link"
                  >
                    {t("contact.email")}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="mt-1 text-[#3d4f42]" />
                  <a
                    href={`tel:${t("contact.phone").replace(/\s/g, "")}`}
                    className="link-underline"
                    data-testid="contact-phone-link"
                  >
                    {t("contact.phone")}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock size={16} className="mt-1 text-[#3d4f42]" />
                  <span>{t("contact.hours")}</span>
                </li>
              </ul>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            onSubmit={submit}
            className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-[#1a211c]/10"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid grid-cols-1 gap-8">
              <div>
                <label className="text-[0.72rem] uppercase tracking-[0.24em] text-[#3d4f42] font-mono">
                  {t("contact.form.nameLabel")}
                </label>
                <input
                  type="text"
                  className="mrx-input"
                  placeholder={t("contact.form.namePlaceholder")}
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-input-name"
                  autoComplete="name"
                />
                {errors.name && (
                  <div className="text-[0.78rem] text-[#b86b43] mt-1.5" data-testid="error-name">
                    {errors.name}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[0.72rem] uppercase tracking-[0.24em] text-[#3d4f42] font-mono">
                  {t("contact.form.emailLabel")}
                </label>
                <input
                  type="email"
                  className="mrx-input"
                  placeholder={t("contact.form.emailPlaceholder")}
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  autoComplete="email"
                />
                {errors.email && (
                  <div className="text-[0.78rem] text-[#b86b43] mt-1.5" data-testid="error-email">
                    {errors.email}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[0.72rem] uppercase tracking-[0.24em] text-[#3d4f42] font-mono">
                  {t("contact.form.messageLabel")}
                </label>
                <textarea
                  className="mrx-input resize-none"
                  rows={5}
                  placeholder={t("contact.form.messagePlaceholder")}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                />
                {errors.message && (
                  <div
                    className="text-[0.78rem] text-[#b86b43] mt-1.5"
                    data-testid="error-message"
                  >
                    {errors.message}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
                <p className="text-[0.78rem] text-[#1a211c]/50 max-w-md">
                  {t("footer.riskNotice")}
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-olive"
                  data-testid="contact-submit"
                >
                  {sending ? t("contact.form.sending") : t("contact.form.submit")}
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
